/** 每个页面共用一个客服加载器；部分初始化的 SDK 不重复执行，避免全局状态冲突。 */
export function createCustomerService({ host, document, notify, timeoutMs = 10000 }) {
  let script = null;
  let pending = null;
  let finish = null;

  // 首屏异步加载；下载失败移除标签，下次点击重新请求。
  function preload() {
    if (script || host.$53) return;
    const url = host.__INITIAL_SITE_CONFIG__?.config?.kf53Url;
    if (!url) throw new Error('Missing kf53Url');
    const element = document.createElement('script');
    element.src = url;
    element.async = true;
    script = element;
    element.onerror = () => {
      element.remove();
      if (script === element) script = null;
      finish?.(false, '客服加载失败，请点击重试。');
    };
    document.head.appendChild(element);
  }

  function isReady() {
    const sdk = host.$53;
    // new 模式使用咨询地址，不依赖 PC 或手机浮窗的打开函数。
    return typeof sdk?.createApi === 'function' && Boolean(sdk.isOnload) &&
      typeof sdk.openurl === 'string' && sdk.openurl.trim() !== '';
  }

  // 连续点击共用同一等待任务，就绪后只打开一次对话。
  function open() {
    if (pending) return pending;
    let resolve;
    const result = new Promise(done => { resolve = done; });
    pending = result;
    let pollTimer;
    let deadlineTimer;
    finish = (success, message) => {
      host.clearInterval(pollTimer);
      host.clearTimeout(deadlineTimer);
      pending = null;
      finish = null;
      resolve(success);
      if (message) notify(message);
    };

    function tryOpen() {
      try {
        if (!isReady()) return;
        const api = host.$53.createApi();
        for (const response of [api.push('cmd', 'kfclient'), api.push('type', 'new')]) {
          if (String(response?.code) !== '201') throw new Error('53KF rejected parameters');
        }
        if (String(api.query()?.code) !== '201') throw new Error('53KF rejected chat request');
        finish(true);
      } catch (error) {
        host.console.error('53KF chat failed:', error);
        finish(false, '客服暂时无法打开，请重试；若仍失败，请刷新页面。');
      }
    }

    // 超时任务独立于 SDK 调用，SDK 抛错也能释放等待状态，允许用户再次点击。
    deadlineTimer = host.setTimeout(() => {
      finish(false, '客服加载超时，请重试；若仍失败，请刷新页面。');
    }, timeoutMs);
    pollTimer = host.setInterval(tryOpen, 100);
    try {
      preload();
      tryOpen();
    } catch (error) {
      host.console.error('53KF loading failed:', error);
      finish(false, '客服暂时不可用，请稍后重试。');
    }
    return result;
  }

  return { preload, open };
}

let service;

// 应用启动时初始化；notify 由页面层注入，统一展示用户可理解的错误提示。
export function initializeCustomerService(notify) {
  if (service) return;
  service = createCustomerService({ host: window, document, notify });
  window.openCustomerService = openCustomerService;
  // 兼容旧富文本的咨询按钮，使用原生事件委托，不依赖 jQuery 和统计脚本。
  // .popupMantis 是历史富文本内容（CMS 数据）中的遗留类名，仅作旧内容兼容；
  // 新富文本请使用 .popup-customer-service。旧内容清理完成后可移除该选择器。
  document.addEventListener('click', event => {
    const target = event.target.closest?.('.popup-customer-service, .popupMantis');
    if (!target) return;
    event.preventDefault();
    event.stopPropagation();
    const { buriedname, buriedvalue } = target.dataset;
    if (!location.hostname.includes('rc') && buriedname && buriedvalue) {
      try { window._hmt?.push(['_trackEvent', buriedname, 'click', buriedvalue]); }
      catch (error) { console.warn('Baidu Analytics tracking failed:', error); }
    }
    openCustomerService();
  });
  try { service.preload(); }
  catch (error) { console.error('53KF preload failed:', error); }
}

// Vue 页面唯一的客服调用入口；以后更换平台，只需修改本模块。
export function openCustomerService() {
  return service?.open();
}
